import type { Category } from "../../domain/entities.ts";
import { collectionReferenceLimit, collectionReferencePanelView, reconcileReferenceAnchors } from "./collection-reference.ts";
import { contextualHelpView } from "../../ui/contextual-help.ts";

export interface CollectionReferenceCapabilities {
  document: Document;
  categories(): Category[];
  activeCollection(): string | null;
  editable(id: string): boolean;
  navigate(id: string, rootId: string): Promise<void>;
  beforeOpen(): Promise<void | boolean>;
  change(id: string, rootId: string, value: string, anchors?: Category["referenceAnchors"], expected?: string): Promise<void>;
  markLocal(): void;
  sync(): void;
  icon(name: string): string;
  copy(text: string): Promise<void>;
  location(): string;
  toast(message: string, error?: boolean): void;
  enhance?(root: ParentNode): void;
}

export function createCollectionReferenceController(capabilities: CollectionReferenceCapabilities) {
  let openId: string | null = null;
  let editingId: string | null = null;
  let layer: HTMLElement | null = null;
  let returnFocus: HTMLElement | null = null;
  let renderedNote = "";
  let closing = false;
  const drafts = new Map<string, string>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  const saves = new Map<string, Promise<boolean>>();
  const restorations = new Map<string, {anchors: NonNullable<Category["referenceAnchors"]>; expected: string}>();
  let statusTimer: ReturnType<typeof setTimeout> | null = null;
  const categoryFor = (id: string) => capabilities.categories().find(item => item.id === id && !item.deleted && !item.archivedAt);
  const status = (id: string, message: string) => {
    if (statusTimer) clearTimeout(statusTimer);
    const element = layer?.querySelector<HTMLElement>(`[data-collection-reference="${CSS.escape(id)}"] [data-reference-status]`);
    if (element) element.textContent = message;
    if (element && message === "Saved locally") statusTimer = setTimeout(() => { if (element.isConnected && element.textContent === message) element.textContent = ""; }, 1200);
  };

  capabilities.document.addEventListener("visibilitychange", () => {
    if (capabilities.document.visibilityState === "hidden") for (const id of drafts.keys()) void save(id);
  });

  async function save(id: string): Promise<boolean> {
    const timer = timers.get(id);
    if (timer) clearTimeout(timer);
    timers.delete(id);
    const value = drafts.get(id);
    if (value === undefined) return await (saves.get(id) || Promise.resolve(true));
    if (new TextEncoder().encode(value).length > collectionReferenceLimit) { status(id, "Note exceeds 64 KB"); return false; }
    const previous = saves.get(id) || Promise.resolve(true);
    const next = previous.catch(() => false).then(async () => {
      const category = categoryFor(id);
      if (!category || !capabilities.editable(id)) { status(id, "Editing access changed"); return false; }
      if ((category.referenceNote || "") !== value || restorations.has(id)) {
        const restoration = restorations.get(id);
        const anchors = restoration?.anchors || reconcileReferenceAnchors(category, value, () => crypto.randomUUID());
        await capabilities.change(id, category.rootId, value, anchors, restoration?.expected);
        restorations.delete(id);
        category.referenceAnchors = anchors;
        category.referenceNote = value;
        capabilities.markLocal();
        capabilities.sync();
      }
      capabilities.document.querySelectorAll<HTMLElement>(`[data-reference-open="${CSS.escape(id)}"]`).forEach(button => {
        const hasContent = Boolean(category.referenceNote?.trim());
        button.classList.toggle("has-content", hasContent);
        const existing = button.querySelector(".sr-only");
        if (!hasContent) existing?.remove();
        else if (!existing) {
          const hint = capabilities.document.createElement("span");
          hint.className = "sr-only";
          hint.textContent = "Has content";
          button.append(hint);
        }
      });
      if (drafts.get(id) === value) { drafts.delete(id); status(id, "Saved locally"); }
      return true;
    }).catch(() => {
      status(id, "Could not save · try again");
      capabilities.toast("Could not save the collection reference note", true);
      return false;
    });
    saves.set(id, next);
    return await next;
  }

  function renderPanel(focus?: "editor" | "edit" | "close"): void {
    if (!layer || !openId) return;
    const category = categoryFor(openId);
    if (!category) { forceClose(); return; }
    const scroll = layer.querySelector<HTMLElement>(".collection-reference-panel__scroll")?.scrollTop || 0;
    const editable = capabilities.editable(openId);
    if (!editable) editingId = null;
    renderedNote = category.referenceNote || "";
    layer.innerHTML = collectionReferencePanelView(category, { editing: editingId === openId, editable, draft: drafts.get(openId), icon: capabilities.icon });
    if (editable || category.referenceNote?.trim()) {
      const menu = capabilities.document.createElement("details");
      menu.className = "composer-menu reference-note-menu panel-action-menu";
      menu.innerHTML = `<summary class="icon-button" aria-label="Reference note actions" title="Reference note actions">${capabilities.icon("ellipsis")}</summary><div><div class="note-menu-group"><span class="menu-group-label">Use note</span>${(drafts.get(openId) ?? category.referenceNote ?? "").trim() ? `<button type="button" data-reference-copy="introduction">${capabilities.icon("link")} Copy note link</button>` : ""}<button type="button" data-reference-history>Version history</button></div>${editable ? `<div class="note-menu-group"><span class="menu-group-label">Templates</span><button type="button" data-note-templates="reference">Use template</button><button type="button" data-save-note-template="reference" ${(drafts.get(openId) ?? category.referenceNote ?? "").trim() ? "" : "disabled"}>Save as note template</button></div>` : ""}<div class="note-menu-group"><span class="menu-group-label">Import & export</span><button type="button" data-note-export="reference">Export Markdown</button>${editable ? '<button type="button" data-note-import="reference">Import Markdown</button>' : ""}</div></div>`;
      layer.querySelector("button[data-reference-close]")?.before(menu);
    }
    const selectionActions = layer.querySelector(".collection-reference__editor-footer, .collection-reference__actions");
    if (selectionActions) {
      selectionActions.insertAdjacentHTML("beforebegin", contextualHelpView("reference"));
      const action = capabilities.document.createElement("button");
      action.type = "button"; action.className = "text-button";
      action.dataset.contextSelection = "reference";
      action.textContent = "Create task from selection"; action.disabled = true; action.hidden = true;
      selectionActions.prepend(action);
    }
    capabilities.enhance?.(layer);
    const scroller = layer.querySelector<HTMLElement>(".collection-reference-panel__scroll");
    if (scroller) scroller.scrollTop = scroll;
    layer.querySelectorAll<HTMLElement>("[data-reference-close]").forEach(button => button.addEventListener("click", () => { void close(); }));
    layer.querySelector<HTMLElement>("[data-reference-edit]")?.addEventListener("click", () => {
      if (!openId || !capabilities.editable(openId)) return;
      editingId = openId;
      renderPanel("editor");
    });
    layer.querySelector<HTMLElement>("[data-reference-done]")?.addEventListener("click", () => {
      if (!openId) return;
      const id = openId;
      void save(id).then(saved => { if (saved && openId === id) { editingId = null; renderPanel("edit"); } });
    });
    const input = layer.querySelector<HTMLTextAreaElement>("[data-reference-input]");
    input?.addEventListener("input", () => {
      const id = input.dataset.referenceInput!;
      drafts.set(id, input.value);
      restorations.delete(id);
      const saveTemplate = layer?.querySelector<HTMLButtonElement>("[data-save-note-template]");
      if (saveTemplate) saveTemplate.disabled = !input.value.trim();
      status(id, "Saving…");
      const timer = timers.get(id);
      if (timer) clearTimeout(timer);
      timers.set(id, setTimeout(() => { void save(id); }, 600));
    });
    input?.addEventListener("blur", () => { void save(input.dataset.referenceInput!); });
    layer.querySelectorAll<HTMLElement>("[data-reference-copy]").forEach(button => button.addEventListener("click", () => {
      if (!openId) return;
      const menu = button.closest<HTMLDetailsElement>("details.composer-menu");
      if (menu) { menu.open = false; menu.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true }); }
      const url = new URL(capabilities.location());
      url.search = "";
      url.hash = "";
      url.searchParams.set("collection", openId);
      url.searchParams.set("note", button.dataset.referenceCopy || "introduction");
      void capabilities.copy(url.toString()).then(() => capabilities.toast("Section link copied"), () => capabilities.toast("Could not copy the link", true));
    }));
    if (focus) layer.querySelector<HTMLElement>(focus === "editor" ? "[data-reference-input]" : focus === "edit" ? "[data-reference-edit], button[data-reference-close]" : "button[data-reference-close]")?.focus({ preventScroll: true });
  }

  function forceClose(): void {
    if (statusTimer) { clearTimeout(statusTimer); statusTimer = null; }
    const id = openId;
    if (id) {
      const timer = timers.get(id);
      if (timer) clearTimeout(timer);
      timers.delete(id);
    }
    layer?.remove();
    layer = null;
    openId = null;
    editingId = null;
    closing = false;
    capabilities.document.body.classList.remove("collection-reference-open");
    capabilities.document.querySelector<HTMLElement>("#app")?.removeAttribute("inert");
    const fallback = id ? capabilities.document.querySelector<HTMLElement>(`[data-reference-open="${CSS.escape(id)}"]`) : null;
    (returnFocus?.isConnected ? returnFocus : fallback)?.focus({ preventScroll: true });
    returnFocus = null;
  }

  async function close(skipAnimation = false): Promise<void> {
    if (!openId || closing) return;
    const id = openId;
    if (!(await save(id))) { layer?.querySelector<HTMLElement>("[data-reference-input]")?.focus(); return; }
    if (openId !== id || !layer) return;
    closing = true;
    if (!skipAnimation && !matchMedia("(prefers-reduced-motion: reduce)").matches) {
      layer.classList.add("is-closing");
      await new Promise(resolve => setTimeout(resolve, 260));
    }
    if (openId === id) forceClose();
  }

  async function open(id: string, slug?: string): Promise<void> {
    const category = categoryFor(id);
    if (!category || (!capabilities.editable(id) && !category.referenceNote?.trim())) return;
    if (openId === id) {
      if (slug) layer?.querySelector<HTMLElement>(`#collection-note-${CSS.escape(id)}-${CSS.escape(slug)}`)?.scrollIntoView({ block: "start" });
      return;
    }
    if (openId) await close(true);
    if (openId) return;
    if (await capabilities.beforeOpen() === false) return;
    returnFocus = capabilities.document.activeElement instanceof HTMLElement ? capabilities.document.activeElement : null;
    if (capabilities.activeCollection() !== id) await capabilities.navigate(id, category.rootId);
    if (capabilities.activeCollection() !== id || !categoryFor(id)) return;
    openId = id;
    editingId = category.referenceNote?.trim() ? null : capabilities.editable(id) ? id : null;
    layer = capabilities.document.createElement("section");
    layer.className = "collection-reference-layer is-preparing";
    layer.setAttribute("role", "dialog");
    layer.setAttribute("aria-modal", "true");
    layer.setAttribute("aria-label", `Reference note for ${category.name}`);
    capabilities.document.body.append(layer);
    capabilities.document.body.classList.add("collection-reference-open");
    capabilities.document.querySelector<HTMLElement>("#app")?.setAttribute("inert", "");
    layer.addEventListener("keydown", event => {
      if (event.key === "Escape") {
        event.preventDefault(); event.stopPropagation();
        const menu = layer?.querySelector<HTMLDetailsElement>("details.composer-menu[open]");
        if (menu) { menu.open = false; menu.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true }); }
        else void close();
        return;
      }
      if (event.key !== "Tab" || !layer) return;
      const focusable = Array.from(layer.querySelectorAll<HTMLElement>("button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], summary")).filter(element => !element.closest("[hidden], [inert]") && element.getClientRects().length);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable.at(-1)!;
      if (event.shiftKey && capabilities.document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && capabilities.document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    renderPanel(editingId === id ? "editor" : "close");
    requestAnimationFrame(() => requestAnimationFrame(() => layer?.classList.replace("is-preparing", "is-entering")));
    if (slug) requestAnimationFrame(() => layer?.querySelector<HTMLElement>(`#collection-note-${CSS.escape(id)}-${CSS.escape(slug)}`)?.scrollIntoView({ block: "start" }));
  }

  function refresh(): void {
    if (!openId || closing) return;
    const category = categoryFor(openId);
    if (!category || (capabilities.activeCollection() !== openId) || (!capabilities.editable(openId) && !category.referenceNote?.trim())) { forceClose(); return; }
    if (category.referenceNote !== renderedNote && editingId !== openId && !drafts.has(openId)) renderPanel();
  }

  function bindPage(root: ParentNode): void {
    root.querySelectorAll<HTMLElement>("[data-reference-open]").forEach(button => button.addEventListener("click", () => { void open(button.dataset.referenceOpen!); }));
    root.querySelectorAll<HTMLElement>("[data-reference-result]").forEach(button => button.addEventListener("click", () => { void open(button.dataset.referenceResult!); }));
  }

  function templateContext() {
    const category = openId ? categoryFor(openId) : undefined;
    if (!category || !openId || closing) return null;
    const input = layer?.querySelector<HTMLTextAreaElement>("[data-reference-input]");
    return { id: openId, rootId: category.rootId, title: category.name, body: input?.value ?? drafts.get(openId) ?? category.referenceNote ?? "", editable: capabilities.editable(openId), start: input?.selectionStart, end: input?.selectionEnd };
  }

  function setTemplateBody(id: string, body: string): void {
    if (openId !== id || closing || !categoryFor(id) || !capabilities.editable(id)) throw new Error("Editing access changed");
    if (new TextEncoder().encode(body).length > collectionReferenceLimit) throw new Error("Note exceeds 64 KB");
    restorations.delete(id); drafts.set(id, body); editingId = id; renderPanel();
  }

  async function restoreVersion(id: string, body: string, anchors: NonNullable<Category["referenceAnchors"]>): Promise<boolean> {
    const target = templateContext();
    if (!target || target.id !== id || !target.editable || new TextEncoder().encode(body).length > collectionReferenceLimit) return false;
    restorations.set(id, { anchors, expected: target.body });
    drafts.set(id, body);
    const saved = await save(id);
    if (saved) { editingId = null; renderPanel(); }
    return saved;
  }
  return { bindPage, open, close, save, refresh, templateContext, setTemplateBody, restoreVersion, openId: () => openId };
}
